import { default as config } from "common/config/config";
import { useQuery } from "react-query";
import type { UseQueryResult } from "react-query";
import type { PaginatedPartnersDto } from "common/models/api/models/PaginatedPartnersDto";
import { RoleType } from "common/models/role/Role";
import useUserState from "ui/adapter/state/useUserState";
import { Queries } from "ui/query/queries";
import type { UseCustomQueryOptions } from "ui/query/react-query-overrides";
import type { PartnerType } from "domain/partner/Partner.types";
import { partnersApi } from "../api/partnersApi";

export type UsePartnerFindAllWithoutParentOptions = {
    query?: string;
    pageSize?: number;
    page: number;
    order?: "ASC" | "DESC";
    type?: PartnerType;
    orderBy?: string;
    organizationAddress?: string;
    archived?: boolean;
};

export default function usePartnerFindAllWithoutParent(
    {
        query,
        pageSize = config.paginatedPageSizes.partners,
        page,
        order,
        orderBy,
        organizationAddress,
        archived,
        type,
    }: UsePartnerFindAllWithoutParentOptions,
    {
        enabled = true,
        ...options
    }: UseCustomQueryOptions<PaginatedPartnersDto, unknown, PaginatedPartnersDto, (string | undefined | number)[]> = {},
): UseQueryResult<PaginatedPartnersDto> {
    const { selectedRole } = useUserState();
    const orgAddress = organizationAddress ?? selectedRole?.resourceAddress;

    return useQuery(
        [Queries.PARTNER_FIND_ALL_WITHOUT_PARENT, query, page, pageSize, order, orderBy, organizationAddress],
        async () => {
            const res = await partnersApi.findAllWithoutParent({
                page,
                pageSize,
                query,
                order,
                orderBy,
                organizationAddress: orgAddress,
                archived,
                type,
            });
            return res.data;
        },
        {
            enabled:
                enabled &&
                (selectedRole?.roleType === RoleType.ORGANIZATION_OWNER ||
                    selectedRole?.roleType === RoleType.OPERATION_MANAGER ||
                    selectedRole?.roleType === RoleType.CUSTOMER_SERVICE),
            ...options,
        },
    );
}
